import React, { useState } from "react";
import {
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Divider,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LanguageIcon from "@mui/icons-material/Language";

const OneLoginApp = ({ selectedApp, handleCloseSidebar }) => {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const handleConnect = () => {
    console.log("Connecting with:", { clientId, clientSecret });
  };

  const handleAuthorize = () => {
    const clientId = "YOUR_CLIENT_ID";
    const redirectUri = "YOUR_REDIRECT_URI";
    const authorizationUrl = `https://YOUR_OAUTH2_PROVIDER/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=YOUR_SCOPES`;

    window.location.href = authorizationUrl;
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Box
      sx={{
        padding: "2rem",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        border: "1px solid #E0E0E0",
        maxWidth: "520px",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Header Section */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        OneLogin Integration
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Securely manage your OneLogin integration settings with this guided setup.
      </Typography>

      {/* Step Indicator with Icon */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Typography fontWeight="bold" color="primary">
          Connection Settings
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Instructions Toggle */}
      <Button
        startIcon={<InfoOutlinedIcon />}
        onClick={() => setShowInstructions(!showInstructions)}
        variant="outlined"
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: "500",
          mb: 2,
        }}
      >
        How to Connect
      </Button>

      {/* Collapsible Instructions Section */}
      <Collapse in={showInstructions}>
        <Box
          sx={{
            backgroundColor: "#f7f9fc",
            padding: "1.5rem",
            borderRadius: "12px",
            border: "1px solid #ddd",
            boxShadow: "inset 0 0 10px rgba(0,0,0,0.05)",
            mb: 2,
          }}
        >
          <Typography variant="h6" gutterBottom fontWeight="bold">
            How to Connect OneLogin
          </Typography>
          <Typography variant="body2" mb={2}>
            Follow these steps to securely integrate your OneLogin account:
          </Typography>
          <ol style={{ paddingLeft: "1.5rem", lineHeight: "1.8" }}>
            <li>Log in to your OneLogin admin account.</li>
            <li>Navigate to <strong>Apps → API → Tokens</strong>.</li>
            <li>Create a new API token and copy the Client ID and Client Secret.</li>
            <li>Paste the Client ID and Client Secret below.</li>
          </ol>
        </Box>
      </Collapse>

      {/* Tabs for Manual Entry and OAuth2 Authorization */}
      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Manual Entry" />
        <Tab label="OAuth2 Authorization" />
      </Tabs>

      {tabIndex === 0 && (
        <>
          {/* Client ID Input */}
          <TextField
            label="Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder="Paste your OneLogin Client ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Client Secret Input with Show/Hide and Lock Icon */}
          <TextField
            label="Client Secret"
            type={showClientSecret ? "text" : "password"}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder="Paste your OneLogin Client Secret"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <IconButton onClick={() => setShowClientSecret(!showClientSecret)} edge="end">
                  {showClientSecret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </>
      )}

      {tabIndex === 1 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click the button below to authorize with OneLogin.
        </Typography>
      )}

      {/* Button Group - Connect/Authorize and Cancel */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={handleCloseSidebar}
          variant="outlined"
          sx={{ padding: "0.6rem 1.5rem", borderRadius: "8px" }}
        >
          Cancel
        </Button>
        {tabIndex === 0 ? (
          <Button
            onClick={handleConnect}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "600",
              padding: "0.6rem 1.2rem",
            }}
          >
            Connect
          </Button>
        ) : (
          <Button
            onClick={handleAuthorize}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "600",
              padding: "0.6rem 1.2rem",
            }}
          >
            Authorize
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OneLoginApp;